import SearchIcon from '@mui/icons-material/Search'
import React, { useState } from 'react'
import { Button, InputAdornment, TextField } from '@mui/material'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { groupsByIdAtom, productTocAtom } from 'shared/api/masters'
import { GroupType } from '../../resources/products/types'
import { Table } from 'shared/ui/Table/Table'

interface GroupSelectionProps {
  onSelect(group: GroupType): void
  onCancel?(): void
  selectedGroupId?: number
}

export function GroupSelection({
  onCancel,
  onSelect,
  selectedGroupId,
}: GroupSelectionProps) {
  const { groupKeys, grouped } = useAtomValue(productTocAtom)
  const groups = useAtomValue(groupsByIdAtom)
  const [searchValue, setSearchValue] = useState('')

  const searchTerms = searchValue
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  const filteredGroupKeys = groupKeys.filter((groupId) => {
    const { name } = groups[groupId] ?? { name: 'NotGrouped' }
    const normalizedName = name.toLowerCase()
    return searchTerms.every((term) => normalizedName.includes(term))
  })

  const columns = [
    {
      title: '',
      width: 100,
      rowContent(id: number) {
        const groupCount = grouped[id]?.length ?? 0
        const { name } = groups[id] ?? { name: 'NotGrouped' }
        const display = `${name} (${groupCount})`
        if (id === selectedGroupId) {
          return <strong>{display}</strong>
        }
        return display
      },
    },
  ]

  const selectedIndex = (index: number) => {
    const groupKey = filteredGroupKeys[index]
    const groupCount = grouped[groupKey]?.length ?? 0
    if (!groupCount) return
    const group =
      groupKey < 0
        ? ({ id: groupKey, name: 'NotGrouped' } as GroupType)
        : groups[groupKey]
    onSelect(group)
  }

  const toolBarItems = [
    ...(onCancel
      ? [
          <Button key="cancel" onClick={onCancel}>
            Cancel
          </Button>,
        ]
      : []),
    <TextField
      key="search"
      size="small"
      label="Search"
      value={searchValue}
      onChange={(event) => setSearchValue(event.target.value)}
      inputProps={{ 'aria-label': 'Search' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />,
  ]

  return (
    <Table
      title="Select Group"
      items={filteredGroupKeys}
      columns={columns}
      onRowClick={selectedIndex}
      toolBarItems={toolBarItems}
    />
  )
}
